import type { AxiosRequestConfig } from "axios";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { ref } from "vue";
import type { PurchaseListResponse } from "../../responses/PurchaseListResponse";
import { api } from "../scripts/api";

export const usePurchaseListStore = defineStore("purchaseList", () => {
    const purchaseLists: Ref<PurchaseListResponse[]> = ref([]);
    const isLoading: Ref<boolean> = ref(false);

    let updatePurchaseListAbortController: AbortController | null = null;

    const loadPurchaseLists = async (): Promise<any> => {
        return new Promise((resolve, reject) => {
            isLoading.value = true;

            try {
                api()
                    .get("/site/purchase-list/")
                    .then((response) => {
                        purchaseLists.value = response.data.data;
                        resolve(response.data.data);
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .finally(() => {
                        isLoading.value = false;
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const createPurchaseList = async (data): Promise<any> => {
        return new Promise((resolve, reject) => {
            try {
                api()
                    .post("/site/purchase-list/", data)
                    .then((response) => {
                        loadPurchaseLists();
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const updatePurchaseList = async (id, data): Promise<any> => {
        return new Promise((resolve, reject) => {
            if (updatePurchaseListAbortController !== null) {
                updatePurchaseListAbortController.abort();
            }

            updatePurchaseListAbortController = new AbortController();

            try {
                api()
                    .put(`/site/purchase-list/${id}/`, data, {
                        signal: updatePurchaseListAbortController.signal,
                    } as AxiosRequestConfig)
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const createPurchaseListItem = async (data): Promise<any> => {
        return new Promise((resolve, reject) => {
            if (updatePurchaseListAbortController !== null) {
                updatePurchaseListAbortController.abort();
            }

            updatePurchaseListAbortController = new AbortController();
            try {
                api()
                    .post("/site/purchase-list-item/", data, {
                        signal: updatePurchaseListAbortController.signal,
                    } as AxiosRequestConfig)
                    .then((response) => {
                        loadPurchaseLists();
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const deletePurchaseList = async (id): Promise<any> => {
        return new Promise((resolve, reject) => {
            try {
                api()
                    .delete(`/site/purchase-list/${id}/`)
                    .then((response) => {
                        loadPurchaseLists();

                        resolve(response.data);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const addPurchaseListToCart = async (id): Promise<any> => {
        return new Promise((resolve, reject) => {
            try {
                api()
                    .post(`/site/purchase-list/${id}/add-to-cart/`)
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const updatePurchaseListItem = async (id, data): Promise<any> => {
        return new Promise((resolve, reject) => {
            try {
                api()
                    .put(`/site/purchase-list-item/${id}/`, data)
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const addPurchaseListItemToCart = async (id): Promise<any> => {
        return new Promise((resolve, reject) => {
            try {
                api()
                    .post(`/site/purchase-list-item/${id}/add-to-cart/`)
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const deletePurchaseListItem = async (id): Promise<any> => {
        return new Promise((resolve, reject) => {
            try {
                api()
                    .delete(`/site/purchase-list-item/${id}/`)
                    .then((response) => {
                        loadPurchaseLists();
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    return {
        purchaseLists,
        isLoading,
        loadPurchaseLists,
        createPurchaseList,
        updatePurchaseList,
        deletePurchaseList,
        addPurchaseListToCart,
        createPurchaseListItem,
        updatePurchaseListItem,
        deletePurchaseListItem,
        addPurchaseListItemToCart,
    };
});
